import { zodResolver } from '@hookform/resolvers/zod'
import { useParams, useLocation } from 'wouter'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import queryString from 'query-string'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'

const formSchema = z.object({
  documentVersionId: z.string().min(1, {
    message: 'Select a document version.',
  }),
})

export const OpenTechnicianModeForm = ({
  onStart,
  onCancel,
}: {
  onStart: () => void
  onCancel: () => void
}) => {
  const [, setLocation] = useLocation()
  const { documentId, projectId } = useParams<{
    projectId: string
    documentId: string
  }>()

  const docPage = useDocumentPageQuery()

  const documentVersions = docPage.data?.documentVersions ?? []
  const isLoadingDocumentVersions = docPage.isLoading
  const latestDocumentVersion = docPage.data?.latestDocumentVersion

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      documentVersionId: '',
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    const qs = queryString.stringify({
      dv: values.documentVersionId,
    })
    setLocation(`~/p/${projectId}/document/${documentId}/tech?${qs}`)
    onStart()
  }

  if (isLoadingDocumentVersions) {
    return <Spinner />
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="w-full max-w-96 flex flex-col space-y-4 justify-self-center">
          <FormField
            control={form.control}
            name="documentVersionId"
            render={({ field }) => (
              <FormItem className="z-[99]">
                <FormLabel>Document Version</FormLabel>
                <FormControl>
                  <select
                    {...field}
                    className="rounded-full z-50 w-full border border-input bg-background px-3 py-2 text-sm shadow-sm"
                  >
                    <option value="">Select a document version</option>
                    {(documentVersions || [])
                      .filter(
                        (version) => version.id !== latestDocumentVersion?.id,
                      )
                      .map((version) => {
                        return (
                          <option key={version.id} value={version.id as string}>
                            {version.name || `V${version.version_number || 1}`}
                          </option>
                        )
                      })}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex items-center space-x-4 justify-self-end z-[99] relative">
          <Button
            type="button"
            variant="ghost"
            className="!text-sm text-gray-500 rounded-full"
            onClick={onCancel}
            size="lg"
          >
            Cancel
          </Button>
          <Button type="submit" className="rounded-full shadow-none" size="lg">
            Start
          </Button>
        </div>
      </form>
    </Form>
  )
}
